import {isFeatureEnabled} from '@github-ui/feature-flags'
import {TransactionalMessageBanner} from '@github-ui/in-product-targeting'
import {VIEW_IDS} from '@github-ui/issue-url-helper/constants/view-constants'
import type {IssueViewerQueries} from '@github-ui/issue-viewer/IssueViewer'
import {JobInfoWithSubscription} from '@github-ui/issues-bulk-actions/JobInfoWithSubscription'
/* eslint-disable-next-line @github-ui/github-monorepo/no-use-app-payload --
   useAppPayload is deprecated. See
   https://thehub.github.com/epd/engineering/dev-practicals/frontend/react/data-router/migration/app-payload-deprecation/ */
import {useAppPayload} from '@github-ui/react-core/use-app-payload'
import {useCurrentRouteState} from '@github-ui/react-core/use-current-route-state'
import {Box} from '@primer/styled-react'
import {useEffect} from 'react'
import {type EntryPointComponent, useLazyLoadQuery} from 'react-relay'

import {IssueDetail} from '../components/show/IssueDetail'
import {StaleCacheBanner} from '../components/StaleCacheBanner'
import {useQueryBulk, useQueryContext} from '../contexts/QueryContext'
import {IssueIndexPage, IssueRepoNewChoosePage, IssueRepoNewPage} from '../lazy-routes'
import type {AppPayload} from '../types/app-payload'
import type {ClientSideRelayDataGeneratorViewQuery} from './__generated__/ClientSideRelayDataGeneratorViewQuery.graphql'
import {AnalyticsWrapper} from './AnalyticsWrapper'
import {currentViewQuery as currentView} from './ClientSideRelayDataGenerator'

export const IssueShowPage: EntryPointComponent<IssueViewerQueries, Record<string, never>> = ({
  queries: {...issueViewerPreloadedQueries},
}) => {
  const {scoped_repository, transactionalMessageBanner} = useAppPayload<AppPayload>()
  const {currentViewId, setCurrentViewId} = useQueryContext()
  const {bulkJobId, setBulkJobId} = useQueryBulk()

  const viewId = selectViewId()
  if (scoped_repository && !currentViewId) {
    setCurrentViewId(viewId)
  }

  const currentViewNode = useLazyLoadQuery<ClientSideRelayDataGeneratorViewQuery>(
    currentView,
    {id: viewId},
    {fetchPolicy: 'store-only'},
  )

  useEffect(() => {
    IssueIndexPage.preload()
    IssueRepoNewPage.preload()
    IssueRepoNewChoosePage.preload()
  }, [])

  // Setting the title to an empty string will prevent the title from being updated by use-title-manager.ts in react-core
  const state = useCurrentRouteState<{type: 'loaded'; data: unknown; title: string}>()
  state.title = ''

  if (!currentViewNode || !currentViewNode.node) {
    return null
  }

  return (
    <AnalyticsWrapper category="Issue Show">
      {isFeatureEnabled('ipm_global_transactional_message_issues') && transactionalMessageBanner ? (
        <TransactionalMessageBanner banner={transactionalMessageBanner} rounded={false} />
      ) : null}
      <StaleCacheBanner />
      <JobInfoWithSubscription bulkJobId={bulkJobId} setBulkJobId={setBulkJobId}>
        <Box sx={{display: 'flex', height: '100%', justifyContent: 'stretch', '> *': {width: '100%'}}}>
          <IssueDetail fetchedView={currentViewNode.node} preloadedQueries={issueViewerPreloadedQueries} />
        </Box>
      </JobInfoWithSubscription>
    </AnalyticsWrapper>
  )

  function selectViewId() {
    if (currentViewId) {
      return currentViewId
    }
    return scoped_repository ? VIEW_IDS.repository : VIEW_IDS.assignedToMe
  }
}
